import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../../style/DonorInventory.css";

const DIVERTED_COLOR = "#669d2e";
const UNREALIZED_COLOR = "#e0a030";

const getBaseline = (acqCost, refPrice, listingCost) => acqCost || refPrice || listingCost || 0;

const DonorCostBreakdown = ({ transactions = [], expiredListings = [], darkMode }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const map = {};

    for (const txn of transactions) {
      const mat = txn.material || "Unknown";
      if (!map[mat]) map[mat] = { material: mat, diverted: 0, unrealized: 0 };
      const baseline = getBaseline(txn.acquisitionCost, txn.referencePricePerLb, txn.listingCost);
      const qty = txn.quantity || 0;
      const accepted = txn.quantityAccepted != null ? txn.quantityAccepted : qty;

      map[mat].diverted += baseline * accepted;
      map[mat].unrealized += baseline * (qty - accepted);
    }

    for (const exp of expiredListings) {
      const mat = exp.material || "Unknown";
      if (!map[mat]) map[mat] = { material: mat, diverted: 0, unrealized: 0 };
      const baseline = getBaseline(exp.acquisitionCost, exp.referencePricePerLb, exp.cost);
      map[mat].unrealized += baseline * (exp.remainingQty || 0);
    }

    return Object.values(map).sort((a, b) => (b.diverted + b.unrealized) - (a.diverted + a.unrealized));
  }, [transactions, expiredListings]);

  const totals = useMemo(() => {
    const t = { diverted: 0, unrealized: 0 };
    for (const d of chartData) {
      t.diverted += d.diverted;
      t.unrealized += d.unrealized;
    }
    return t;
  }, [chartData]);

  if (chartData.length === 0) return null;

  const axisColor = darkMode ? "#aaaaaa" : "#666666";
  const gridColor = darkMode ? "#444444" : "#e0e0e0";
  const fmt = (v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const cursorStyle = {
    fill: darkMode ? "rgba(102, 157, 46, 0.25)" : "rgba(102, 157, 46, 0.12)",
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const total = d.diverted + d.unrealized;
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip-label">{d.material}</p>
        <p>{t("Diverted")}: {fmt(d.diverted)}</p>
        <p>{t("Unrealized")}: {fmt(d.unrealized)}</p>
        <p>{t("Total")}: {fmt(total)}</p>
      </div>
    );
  };

  const needsVertical = chartData.length > 4;

  // custom tick aligns label end to bar bottom
  const VerticalTick = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" transform="rotate(-90)" fill={axisColor} fontSize={11}>
        {payload.value}
      </text>
    </g>
  );

  return (
    <div className="chart-card chart-card--full">
      <div className="chart-card-header">
        <div className="chart-card-icon green">
          <FontAwesomeIcon icon={faChartLine} />
        </div>
        <h5 className="chart-card-title">{t("Value Breakdown by Material")}</h5>
        <span className="chart-card-badge">
          {chartData.length} {t("materials")}
        </span>
      </div>

      <div className="chart-card-content">
        <ResponsiveContainer width="100%" height={needsVertical ? 380 : 280}>
          <BarChart data={chartData} margin={{ top: 30, right: 20, left: 10, bottom: needsVertical ? 120 : 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="material"
              stroke={axisColor}
              fontSize={11}
              interval={0}
              tick={needsVertical ? <VerticalTick /> : undefined}
            />
            <YAxis stroke={axisColor} fontSize={11} tickFormatter={fmt} />
            <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
            <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
            <Bar
              dataKey="diverted"
              name={t("Value Diverted")}
              stackId="cost"
              fill={DIVERTED_COLOR}
              radius={[0, 0, 0, 0]}
              maxBarSize={50}
            />
            <Bar
              dataKey="unrealized"
              name={t("Unrealized Value")}
              stackId="cost"
              fill={UNREALIZED_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-summary">
        <div className="summary-item">
          <span className="summary-label">{t("Total Diverted")}</span>
          <span className="summary-value">{fmt(totals.diverted)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">{t("Unrealized Value")}</span>
          <span className="summary-value">{fmt(totals.unrealized)}</span>
        </div>
      </div>
    </div>
  );
};

export default DonorCostBreakdown;
